import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { fmtDate } from "./adminUtils";

const Section = ({ title, children, testId }) => (
  <section className="bg-card border border-border rounded-sm p-5 space-y-4" data-testid={testId}>
    <h3 className="font-heading text-lg">{title}</h3>
    {children}
  </section>
);

const StatCard = ({ label, value }) => (
  <div className="bg-secondary/50 border border-border rounded-sm p-4">
    <p className="text-2xl font-heading tabular-nums">{value}</p>
    <p className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{label}</p>
  </div>
);

export default function AdminOrganizationDetail() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/organizations/${orgId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [orgId]);

  if (loading) return <p className="font-mono text-sm text-muted-foreground">Loading…</p>;
  if (!data) return <p className="font-mono text-sm text-muted-foreground">Organization not found.</p>;

  const g = data.general;
  const s = data.statistics;

  return (
    <div className="space-y-6" data-testid="admin-org-detail-page">
      <Button variant="ghost" size="sm" className="rounded-sm -ml-2" onClick={() => navigate("/admin/organizations")} data-testid="admin-org-back">
        <ArrowLeft size={16} className="mr-2" /> All organizations
      </Button>

      <PageHeader title={g.name} subtitle={`Organization details • ${g.slug || "no-slug"}`} />

      <Section title="General Information" testId="admin-org-general">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div><p className="text-muted-foreground text-xs uppercase">ID</p><p className="font-mono text-xs break-all">{g.id}</p></div>
          <div><p className="text-muted-foreground text-xs uppercase">Slug</p><p className="font-mono">{g.slug || "—"}</p></div>
          <div><p className="text-muted-foreground text-xs uppercase">Region</p><p>{g.region || "—"}</p></div>
          <div><p className="text-muted-foreground text-xs uppercase">Created</p><p>{fmtDate(g.created_at)}</p></div>
          <div><p className="text-muted-foreground text-xs uppercase">Status</p><Badge variant={g.status === "active" ? "default" : "secondary"} className="rounded-sm text-[10px] uppercase">{g.status}</Badge></div>
          <div><p className="text-muted-foreground text-xs uppercase">Marketing consent</p><p>{g.consent_marketing ? "Yes" : "No"}</p></div>
        </div>
      </Section>

      <Section title="Statistics" testId="admin-org-statistics">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard label="Members" value={s.members} />
          <StatCard label="Social Accounts" value={s.social_accounts} />
          <StatCard label="Ecommerce" value={s.ecommerce_accounts} />
          <StatCard label="Active API Keys" value={s.api_keys} />
          <StatCard label="Products" value={s.products} />
          <StatCard label="Social Posts" value={s.social_posts} />
          <StatCard label="Campaigns" value={s.campaigns} />
          <StatCard label="Promotions" value={s.promotions} />
          <StatCard label="Knowledge Bases" value={s.knowledge_bases} />
        </div>
      </Section>

      <Section title={`Members (${data.members.length})`} testId="admin-org-members">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.members.map((m) => (
                <TableRow key={m.user_id} data-testid={`admin-org-member-${m.email}`}>
                  <TableCell className="font-medium">{m.name}</TableCell>
                  <TableCell className="font-mono text-xs">{m.email}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm text-[10px] uppercase">{m.role}</Badge></TableCell>
                  <TableCell><Badge variant={m.status === "active" ? "default" : "secondary"} className="rounded-sm text-[10px] uppercase">{m.status}</Badge></TableCell>
                </TableRow>
              ))}
              {data.members.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-4">No members</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Section>

      <Section title={`Connected Social Accounts (${data.social_accounts.length})`} testId="admin-org-social">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Platform</TableHead><TableHead>Handle</TableHead><TableHead>Connector</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.social_accounts.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="capitalize">{a.platform}</TableCell>
                  <TableCell className="font-mono text-xs">{a.handle || a.username || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{a.connector_id}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm text-[10px] uppercase">{a.status || "—"}</Badge></TableCell>
                </TableRow>
              ))}
              {data.social_accounts.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-4">No social accounts</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Section>

      <Section title={`Connected Ecommerce Accounts (${data.ecommerce_accounts.length})`} testId="admin-org-ecommerce">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Connector</TableHead><TableHead>Connection ID</TableHead><TableHead>Created</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.ecommerce_accounts.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="capitalize">{c.connector_id}</TableCell>
                  <TableCell className="font-mono text-xs break-all">{c.id}</TableCell>
                  <TableCell className="text-sm">{fmtDate(c.created_at)}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm text-[10px] uppercase">{c.status || "—"}</Badge></TableCell>
                </TableRow>
              ))}
              {data.ecommerce_accounts.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-4">No ecommerce accounts</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Section>

      <Section title={`API Keys (${data.api_keys.length})`} testId="admin-org-apikeys">
        <p className="text-xs text-muted-foreground -mt-2">Metadata only — secret values are never exposed.</p>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Prefix</TableHead><TableHead>Created</TableHead><TableHead>Last Used</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.api_keys.map((k) => (
                <TableRow key={k.id}>
                  <TableCell className="font-medium">{k.name}</TableCell>
                  <TableCell className="font-mono text-xs">{k.prefix ? `${k.prefix}…` : "—"}</TableCell>
                  <TableCell className="text-sm">{fmtDate(k.created_at)}</TableCell>
                  <TableCell className="text-sm">{fmtDate(k.last_used_at)}</TableCell>
                  <TableCell><Badge variant={k.revoked ? "secondary" : "default"} className="rounded-sm text-[10px] uppercase">{k.revoked ? "revoked" : "active"}</Badge></TableCell>
                </TableRow>
              ))}
              {data.api_keys.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4">No API keys</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Section>
    </div>
  );
}
